import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, MapPin, Package } from 'lucide-react'
import { routes } from '@/shared/constants'
import { findLocationNames, type Country } from '@/shared/hooks/useCountries'
import { BusinessLogoMark } from './BusinessLogoMark'
import { BusinessStatusBadge } from './BusinessStatusBadge'
import type { BusinessListItem as BusinessListItemType } from '../hooks/useMyBusinesses'

interface BusinessListItemProps {
  business: BusinessListItemType
  countries: Country[]
  activeServicesCount: number
}

export function BusinessListItem({
  business,
  countries,
  activeServicesCount,
}: BusinessListItemProps) {
  const addressSummary = useMemo(() => {
    const main = business.addresses?.find((a) => a.isMain) ?? business.addresses?.[0]
    if (!main) return null
    const loc = findLocationNames(countries, {
      countryId: main.countryId,
      provinceId: main.provinceId,
      municipalityId: main.municipalityId,
    })
    const region = [loc.municipalityName, loc.provinceName]
      .filter((x) => x && x !== '—')
      .join(', ')
    const summary = [main.street1?.trim(), region].filter(Boolean).join(' · ')
    return summary || main.city?.trim() || null
  }, [business.addresses, countries])

  return (
    <Link
      to={routes.app.businesses.detail(business.id)}
      className="flex items-center gap-4 rounded-lg px-2 py-3 transition-colors hover:bg-surface focus-visible:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
    >
      <BusinessLogoMark id={business.id} name={business.legalName} logo={business.logo} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{business.legalName}</p>
        {addressSummary && (
          <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-ink-muted">
            <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{addressSummary}</span>
          </p>
        )}
        <p className="mt-0.5 flex items-center gap-1 text-xs text-ink-subtle">
          <Package className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {activeServicesCount} {activeServicesCount === 1 ? 'servicio activo' : 'servicios activos'}
        </p>
      </div>
      <BusinessStatusBadge status={business.status} />
      <ChevronRight className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
    </Link>
  )
}
